/**
 *
 * @module sessionClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/session.md
 *
 */

/* eslint-disable camelcase */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import Token from './../token/entry';
import HttpClient from './../platforms/httpClient';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import SessionClientConfiguration from './sessionClientConfiguration';
import SessionClientEndpoint from './sessionClientEndpoint';
import SessionAccountInfo from './sessionAccountInfo';
import SessionDeviceInfo from './sessionDeviceInfo';
import SessionProfileInfo from './sessionProfileInfo';
import SessionEntitlement from './sessionEntitlement';
import SessionInfo from './sessionInfo';
import SessionLocation from './sessionLocation';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const SessionClientDustUrnReference = DustUrnReference.services.session.sessionClient;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class SessionClient
 *
 */
export default class SessionClient {

    /**
     *
     * @param {SessionClientConfiguration} sessionClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(sessionClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'SessionClient(sessionClientConfiguration, logger, httpClient)',
            get sessionClientConfiguration() {
                return `${this.ctor} needs SessionClientConfiguration instance`;
            }
        };

        instanceStrict(
            sessionClientConfiguration,
            SessionClientConfiguration,
            errorMessage.sessionClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {SessionClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = sessionClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(SessionClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @since 3.1.0
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<SessionInfo>}
     * @desc Gets device, location, and entitlement information on the current session.
     * @note intermediary variables are used in order to construct the SessionInfo object with the correct
     * variable names from the spec and to avoid collisions for fields with the same name in the spec.
     *
     */
    getInfo(accessToken) {

        const errorMessage = {
            method: 'SessionClient.getInfo(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            SessionClientEndpoint.getInfo
        );

        logger.info(SessionClient.name, 'Attempting to get session information.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SessionClient.name,
            urn: SessionClientDustUrnReference.getInfo,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            // Warning: destructuring reassigns a varname (location) that would otherwise reference a global var.
            const { session_id: sessionInfoId, account, device, location, profile, entitlements } = data;
            const { inSupportedLocation } = data;
            const { id, platform } = device;
            const { type, zip_code, country_code, dma } = location;

            const sessionAccountInfo = account ? new SessionAccountInfo(account.id) : undefined;
            const sessionDeviceInfo = new SessionDeviceInfo(id, platform);
            const sessionLocation = new SessionLocation(type, zip_code, country_code, dma);
            const sessionProfileInfo = profile ? new SessionProfileInfo(profile.id) : undefined;

            const entitlementInstances = entitlements.map((entitlement) => {

                const { name } = entitlement;

                return new SessionEntitlement(name);

            });

            return Promise.resolve(
                new SessionInfo({
                    id: sessionInfoId,
                    account: sessionAccountInfo,
                    device: sessionDeviceInfo,
                    location: sessionLocation,
                    profile: sessionProfileInfo,
                    entitlements: entitlementInstances,
                    inSupportedLocation
                })
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {String} rel
     * @param {Object} body
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/session/sessionClient.js